import styled, { css, keyframes } from "styled-components";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

type CakeNameProps = {
    color: keyof IColors;
};

export const SelectCakeContainer = styled.div(({ theme }) => ({
    padding: `${theme.spacing.xLarge} ${theme.spacing.medium} ${theme.spacing.xxLarge} ${theme.spacing.medium}`,
    paddingBottom: `calc(var(--sab, 0px) + ${theme.spacing.xxLarge})`,
    backgroundColor: theme.colors[theme.colorMode].background_Button_Tertiary_Low,
}));

export const SelectCakeMessageContainer = styled.div(({ theme }) => ({
    paddingBottom: `${theme.spacing.large}`,
}));

export const ItemsContainer = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    padding: `${theme.spacing.medium} 0`,
    borderRadius: theme.cornerRadius.large,
    marginTop: theme.spacing.medium,
    backgroundColor: theme.colors[theme.colorMode].background_Primary,
}));

export const SubItemContainer = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xSmall,
    padding: `0 ${theme.spacing.medium}`,
}));

export const ItemDescWrapper = styled.div(() => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
}));

export const Separator = styled.div(({ theme }) => ({
    borderTop: `1px solid ${theme.colors[theme.colorMode].background_Secondary_Alpha}`,
    margin: `${theme.spacing.medium}  0`,
}));

export const SeparatorDesc = styled.div(({ theme }) => ({
    color: theme.colors[theme.colorMode].text_Low_Emphasis,
    margin: `0 ${theme.spacing.xxxSmall}`,
}));

export const FoodSymbolIcon = styled(FoodSymbolIconSvg)(({ theme }) => ({
    marginTop: theme.spacing.xxSmall,
}));

export const PredefinedWordsContainer = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: `${theme.spacing.small}`,
    overflowX: "scroll",
    scrollbarWidth: "none",
    padding: `0 ${theme.spacing.medium}`,
    "&::-webkit-scrollbar": {
        display: "none",
    },
    "&::-webkit-scrollbar-thumb": {
        display: "none",
    },
}));

export const StyledMessageMaxLimit = styled(Text)<{ limitExceeded: boolean }>(({ theme, limitExceeded }) => ({
    color: limitExceeded ? theme.colors[theme.colorMode].IG_Primary : theme.colors[theme.colorMode].text_Low_Emphasis,
    position: "absolute",
    right: theme.spacing.medium,
    bottom: 0,
    height: "36px",
    display: "flex",
    alignItems: "center",
}));

export const StyledInputContainer = styled.div(({ theme }) => ({
    position: "relative",
    marginTop: `${theme.spacing.xxSmall}`,
    padding: `0 ${theme.spacing.medium}`,
}));

const shake = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
`;

export const StyledInput = styled.input<{ isShaking?: boolean }>`
    border-bottom: ${({ theme }) => `1px solid ${theme.colors[theme.colorMode].background_Button_Primary}`};
    outline: none;
    height: 36px;
    width: 100%;
    margin: 0;
    box-shadow: none;
    caret-color: ${({ theme }) => theme.colors[theme.colorMode].primary};
    color: ${({ theme }) => theme.colors[theme.colorMode].text_Highest_Emphasis};

    ${({ isShaking }) =>
        isShaking &&
        css`
            animation: ${shake} 0.3s;
            border-color: red;
        `}
`;

export const StyledMessageContainer = styled.div(({ theme }) => ({
    width: "100%",
    height: "150px",
    padding: `${theme.spacing.xSmall}`,
}));

export const StyledAddCakeMessage = styled.div(({ theme }) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    overflow: "hidden",
    background: "linear-gradient(90deg, #F0F0F5 42.88%, #FFF 87.21%)",
    borderRadius: `${theme.cornerRadius.large} ${theme.cornerRadius.large} 0 0`,
}));

// added fontstyle manually for the message on cake - design requirement
export const StyledMessageOnCake = styled(Text)(({ theme }) => ({
    width: "50%",
    boxSizing: "border-box",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    padding: `${theme.spacing.xLarge} 0 0 ${theme.spacing.large}`,
    color: "#808CBB",
    fontSize: "22px",
    fontStyle: "italic",
    fontWeight: 600,
    lineHeight: "22px",
    letterSpacing: "-0.6px",
}));

export const StyledImageContainer = styled.div({
    width: "50%",
    boxSizing: "border-box",
    wordWrap: "break-word",
    display: "flex",
    justifyContent: "end",
});

export const StyledButton = styled.div(({ theme }) => ({
    padding: `${theme.spacing.xxLarge} ${theme.spacing.medium} 0 ${theme.spacing.medium}`,
    "@supports (padding-bottom: var(--sab))": {
        paddingBottom: "var(--sab)",
    },
}));

export const StyledImage = styled(Image)(({ theme }) => ({
    borderRadius: theme.cornerRadius.xSmall,
}));

export const ItemContent = styled.div(() => ({
    flex: "1",
}));

export const CakeName = styled.p<CakeNameProps>(({ color, theme }) => ({
    ...theme.typography["V2.body1.neutral.bold"],
    color: theme.colors[theme.colorMode][color],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "168px",
    flex: "0 1 auto",
}));
